import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { FaPlus, FaSearch, FaTrash, FaUserEdit, FaUsers } from "react-icons/fa";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../../services/apiUtils";
import { extractObjectId, toCdnUrl } from "../../../utils/utils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import "./AuthorManagement.css";

const EMPTY_FORM = {
  authorId: "",
  name: "",
  designation: "Music Journalist",
  bio: "",
  instagram: "",
  twitter: "",
  image: "",
};

export default function AuthorManagement() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [authorToDelete, setAuthorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [authorImageIndexMap, setAuthorImageIndexMap] = useState({});
  const [newsAuthorImageMap, setNewsAuthorImageMap] = useState({});

  const normalizeAuthorName = (name = "") =>
    name.trim().toLowerCase().replace(/\s+/g, " ");

  const getApiOrigin = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    if (!baseUrl) return "";
    try {
      return new URL(baseUrl).origin;
    } catch {
      return "";
    }
  };

  const buildImageCandidates = (rawUrl) => {
    if (!rawUrl) return [];
    const value = String(rawUrl).trim();
    if (!value) return [];

    const apiOrigin = getApiOrigin();
    const candidates = [
      toCdnUrl(value),
      value,
    ];

    if (value.startsWith("/")) {
      if (apiOrigin) candidates.push(`${apiOrigin}${value}`);
      candidates.push(`https://lyricsweb.com${value}`);
    } else if (!value.startsWith("http")) {
      if (apiOrigin) candidates.push(`${apiOrigin}/${value.replace(/^\/+/, "")}`);
      candidates.push(`https://lyricsweb.com/${value.replace(/^\/+/, "")}`);
    }

    return [...new Set(candidates.filter(Boolean))];
  };

  const resolveProfileImage = (author) => author?.image || author?.authorPic || author?.avatar || "";

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      const response = await allAPiServicesCall.getAuthors(getAuthConfig());
      setAuthors(response?.data || []);
      setAuthorImageIndexMap({});
    } catch (error) {
      console.error("Error fetching authors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthors();
    fetchNewsAuthorImageFallbacks();
  }, []);

  const fetchNewsAuthorImageFallbacks = async () => {
    try {
      const response = await allAPiServicesCall.getNews({}, getAuthConfig());
      const newsList = Array.isArray(response?.data) ? response.data : [];

      const sortedNews = [...newsList].sort((a, b) => {
        const dateA = new Date(a?.publishDate || a?.updatedAt || a?.createdAt || 0).getTime() || 0;
        const dateB = new Date(b?.publishDate || b?.updatedAt || b?.createdAt || 0).getTime() || 0;
        return dateB - dateA;
      });

      const fallbackMap = {};
      sortedNews.forEach((news) => {
        const key = normalizeAuthorName(news?.author || "");
        const imageUrl = toCdnUrl(news?.authorPic) || news?.authorPic || "";
        if (!key || !imageUrl || fallbackMap[key]) return;
        fallbackMap[key] = imageUrl;
      });

      setNewsAuthorImageMap(fallbackMap);
      setAuthorImageIndexMap({});
    } catch (error) {
      console.error("Error fetching news author image fallbacks:", error);
    }
  };

  const filteredAuthors = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return authors;
    return authors.filter((author) =>
      [author.name, author.designation, author.bio]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(query))
    );
  }, [authors, search]);

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setShowModal(true);
  };

  const openEditModal = (author) => {
    setForm({
      authorId: extractObjectId(author._id),
      name: author.name || "",
      designation: author.designation || "Music Journalist",
      bio: author.bio || "",
      instagram: author.instagram || "",
      twitter: author.twitter || "",
      image: resolveProfileImage(author),
    });
    setImageFile(null);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setForm(EMPTY_FORM);
    setImageFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return form.image;
    const uploadData = new FormData();
    uploadData.append("image", imageFile);
    const response = await allAPiServicesCall.uploadAuthorPersonaImage(
      uploadData,
      getFormDataConfig()
    );
    return response?.data?.path || form.image;
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;

    try {
      setSaving(true);
      const imagePath = await uploadImageIfNeeded();
      const payload = {
        name: form.name.trim(),
        designation: form.designation.trim(),
        bio: form.bio.trim(),
        instagram: form.instagram.trim(),
        twitter: form.twitter.trim(),
        image: imagePath || "",
      };

      if (form.authorId) {
        await allAPiServicesCall.updateAuthor(
          { ...payload, authorId: form.authorId },
          getAuthConfig()
        );
      } else {
        await allAPiServicesCall.addAuthor(payload, getAuthConfig());
      }

      closeModal();
      fetchAuthors();
    } catch (error) {
      console.error("Error saving author:", error);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (author) => {
    setAuthorToDelete(author);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setAuthorToDelete(null);
  };

  const handleDeleteAuthor = async () => {
    const authorId = extractObjectId(authorToDelete?._id);
    if (!authorId) return;
    try {
      setDeleting(true);
      await allAPiServicesCall.deleteAuthor(authorId, getAuthConfig());
      closeDeleteModal();
      fetchAuthors();
    } catch (error) {
      console.error("Error deleting author:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleSeedAuthors = async () => {
    try {
      setSeedLoading(true);
      await allAPiServicesCall.seedAuthorPersonas(getAuthConfig());
      fetchAuthors();
    } catch (error) {
      console.error("Error seeding authors:", error);
    } finally {
      setSeedLoading(false);
    }
  };

  return (
    <CommonLayout>
      <div className="author-management-page container-fluid px-2 px-md-4">
        <div className="am-header">
          <div className="am-header-top">
            <div className="am-title-wrapper">
              <div className="am-title-icon">
                <FaUsers />
              </div>
              <div className="am-title-text">
                <h2 className="am-title mb-0">Author Management</h2>
                <p className="am-subtitle mb-0">
                  Manage writer profiles used in News bylines
                </p>
              </div>
            </div>

            <div className="am-stats">
              <div className="am-stat-badge">
                <FaUsers />
                <span>
                  Total: <strong>{authors.length}</strong>
                </span>
              </div>
            </div>
          </div>

          <div className="am-header-actions">
            <button
              type="button"
              className="am-btn am-btn-ghost"
              onClick={handleSeedAuthors}
              disabled={seedLoading}
            >
              {seedLoading ? "Seeding..." : "Seed Default Authors"}
            </button>
            <button type="button" className="am-btn am-btn-primary" onClick={openCreateModal}>
              <FaPlus className="me-2" />
              Add Author
            </button>
          </div>
        </div>

        <div className="am-filter-section">
          <div className="am-search-box">
            <FaSearch className="search-icon" />
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, designation, bio..."
              className="am-search-input"
            />
          </div>
          <div className="am-filter-info">
            Showing <strong>{filteredAuthors.length}</strong> of <strong>{authors.length}</strong>
          </div>
        </div>

        <div className="am-table-card">
          <div className="am-table-container">
            <Table hover className="mb-0 align-middle am-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Designation</th>
                <th>Bio</th>
                <th>Status</th>
                <th style={{ width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 am-empty-state">
                    Loading authors...
                  </td>
                </tr>
              ) : filteredAuthors.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-4 am-empty-state">
                    No authors found
                  </td>
                </tr>
              ) : (
                filteredAuthors.map((author) => {
                  const authorId = extractObjectId(author._id);
                  const profileImage = resolveProfileImage(author);
                  const newsFallbackImage =
                    newsAuthorImageMap[normalizeAuthorName(author?.name || "")] || "";
                  const imageCandidates = [...new Set([
                    ...buildImageCandidates(profileImage),
                    ...buildImageCandidates(newsFallbackImage),
                  ])];
                  const imageIndex = authorImageIndexMap[authorId] || 0;
                  const authorImage = imageCandidates[imageIndex] || "";

                  return (
                  <tr key={authorId}>
                    <td>
                      <div className="d-flex align-items-center gap-2 am-name-cell">
                        {authorImage ? (
                          <img
                            src={authorImage}
                            alt={author.name}
                            className="author-avatar"
                            onError={() =>
                              setAuthorImageIndexMap((prev) => ({
                                ...prev,
                                [authorId]: imageIndex + 1,
                              }))
                            }
                          />
                        ) : (
                          <div className="author-avatar-placeholder">{(author.name || "?").charAt(0)}</div>
                        )}
                        <span className="fw-semibold">{author.name}</span>
                      </div>
                    </td>
                    <td>{author.designation || "-"}</td>
                    <td className="author-bio-cell">{author.bio || "-"}</td>
                    <td>
                      <span
                        className={`am-status-badge ${
                          author.isActive === false ? "inactive" : "active"
                        }`}
                      >
                        {author.isActive === false ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td>
                      <div className="am-actions">
                        <button
                          type="button"
                          className="am-action-btn am-action-btn-edit"
                          onClick={() => openEditModal(author)}
                          title="Edit Author"
                        >
                          <FaUserEdit />
                        </button>
                        <button
                          type="button"
                          className="am-action-btn am-action-btn-delete"
                          onClick={() => openDeleteModal(author)}
                          title="Delete Author"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
            </Table>
          </div>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        backdrop="static"
        contentClassName="am-modal-content"
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{form.authorId ? "Edit Author" : "Add Author"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleInputChange}
              placeholder="e.g., Ashley Tan"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Designation</Form.Label>
            <Form.Control
              name="designation"
              value={form.designation}
              onChange={handleInputChange}
              placeholder="e.g., Music Journalist"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Bio</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="bio"
              value={form.bio}
              onChange={handleInputChange}
              placeholder="Short professional bio"
            />
          </Form.Group>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Instagram</Form.Label>
                <Form.Control
                  name="instagram"
                  value={form.instagram}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/..."
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Twitter/X</Form.Label>
                <Form.Control
                  name="twitter"
                  value={form.twitter}
                  onChange={handleInputChange}
                  placeholder="https://x.com/..."
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group>
            <Form.Label>Profile Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
            {form.image && !imageFile && (
              <small className="text-muted">Current image already set</small>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className="am-modal-btn am-modal-btn-cancel" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="am-modal-btn am-modal-btn-primary"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? "Saving..." : form.authorId ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        onConfirm={handleDeleteAuthor}
        title="Delete Author"
        message={`Are you sure you want to delete "${authorToDelete?.name || "this author"}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleting}
      />
    </CommonLayout>
  );
}
